import { FC, useMemo } from "react";
import { Box, CircularProgress, IconButton, Link, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { PanelLayout } from "../PanelLayout";
import { useAppSettings } from "../../../app/hooks/useAppSettings";
import { TOnboardingConfig } from "../../../app/models";
import { TVerificationRequest } from "../../models";
import { useStellarInfo } from "../../hooks/useStellarInfo";

const formatTxnId = (txnId: string) => {
  if (txnId.length <= 11) return txnId;
  return `${txnId.slice(0, 4)}...${txnId.slice(-4)}`;
};

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

export const StellarPanel: FC<{
  data: TVerificationRequest | null;
}> = ({ data }) => {
  const [appSettings] = useAppSettings(data?.appId?.toString());
  const [stellarInfo, isLoading, error] = useStellarInfo(data?.id);

  const shouldShowPanel = useMemo(() => {
    if (!appSettings?.onboardingConfig) {
      return false;
    }

    try {
      const config: TOnboardingConfig = JSON.parse(appSettings.onboardingConfig);
      return config.poc_stellar_verifiable_creds === true;
    } catch (e) {
      console.error("Failed to parse onboarding config:", e);
      return false;
    }
  }, [appSettings]);

  if (!shouldShowPanel) {
    return null;
  }

  return (
    <PanelLayout title="Stellar">
      <Box sx={{ mt: 2 }}>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        
        {error && (
          <Typography color="error" variant="body2">
            Error loading Stellar information: {error.message}
          </Typography>
        )}
        
        {stellarInfo && !isLoading && !error && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Stellar Address
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  wordBreak: "break-all",
                  fontFamily: "monospace",
                  fontSize: "0.875rem",
                  fontWeight: 500
                }}
              >
                {stellarInfo.stellarAddress || "No address available"}
              </Typography>
            </Box>

            {stellarInfo.grants && Object.keys(stellarInfo.grants).length > 0 && (
              <>
                {Object.entries(stellarInfo.grants).map(([key, value]) => (
                  <Box key={key}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 500,
                        wordBreak: "break-word"
                      }}
                    >
                      {value || "-"}
                    </Typography>
                  </Box>
                ))}
              </>
            )}

            {stellarInfo.txns && stellarInfo.txns.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Transactions
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {stellarInfo.txns.map((txn, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 1,
                        padding: 1,
                        backgroundColor: "action.hover",
                        borderRadius: 1
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {txn.name}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Link
                            href={`https://stellar.expert/explorer/testnet/tx/${txn.txnId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                              fontFamily: "monospace",
                              fontSize: "0.75rem",
                            }}
                          >
                            {formatTxnId(txn.txnId)}
                          </Link>
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(txn.txnId)}
                            sx={{ padding: 0.5 }}
                          >
                            <ContentCopyIcon sx={{ fontSize: "0.875rem" }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </PanelLayout>
  );
};
